import { queryOptions } from '@tanstack/react-query'

import { ApiClientError } from '@ems/api-client/client'

import {
  fetchApplication,
  fetchApplicationPricing,
  fetchApplications,
  fetchBooths,
  fetchBoothTypes,
} from '#admin/features/exhibitor/api.ts'
import {
  fetchCompanies,
  fetchCompanyLogoUrl,
  fetchTeam,
} from '#admin/features/exhibitor/companies-api.ts'
import { fetchVenue } from '#admin/features/exhibitor/floor-plan/venue-api.ts'
import { fetchBadgeTypes } from '#admin/features/exhibitor/onboarding/api.ts'
import { fetchServiceCategories } from '#admin/features/exhibitor/service-categories-api.ts'
import { fetchCatalog } from '#admin/features/exhibitor/service-items-api.ts'
import {
  fetchFitOut,
  fetchFitOutFileLink,
  fetchPasses,
  type FitOutFile,
  fetchStandOfRegistration,
} from '#admin/features/exhibitor/stand-extras-api.ts'
import {
  fetchServiceItems,
  fetchServiceOrder,
  fetchServiceOrders,
  fetchServicePolicy,
  previewOrder,
  type AdminOrderInput,
} from '#admin/features/exhibitor/service-orders-api.ts'

/**
 * Query options, not hooks — so a route loader can prefetch with the same key the component
 * suspends on. A custom hook can only be called from a component, which is how a list ends up
 * fetching after the route has already rendered its skeleton.
 *
 * Every key starts with `exhibitor`. One invalidation after an action refreshes the list, the
 * detail and the floor together.
 */
export const applicationsQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'applications'],
    queryFn: ({ signal }) => fetchApplications(signal),
  })

export const applicationQuery = (id: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', id],
    queryFn: ({ signal }) => fetchApplication(id, signal),
  })

export const pricingQuery = (id: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', id, 'pricing'],
    queryFn: ({ signal }) => fetchApplicationPricing(id, signal),
  })

export const boothsQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'booths'],
    queryFn: ({ signal }) => fetchBooths(signal),
  })

/**
 * The venue under the floor plan. Its image link lasts five minutes, so the answer is fresh for
 * four, and a focus on the window does not fetch it again: a new link would load the same image
 * twice and blink the plan.
 */
export const venueQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'venue'],
    queryFn: ({ signal }) => fetchVenue(signal),
    staleTime: 4 * 60_000,
    refetchOnWindowFocus: false,
  })

export const boothTypesQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'booth-types'],
    queryFn: ({ signal }) => fetchBoothTypes(signal),
  })

export const serviceOrdersQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'service-orders'],
    queryFn: ({ signal }) => fetchServiceOrders(signal),
  })

export const serviceOrderQuery = (id: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'service-orders', id],
    queryFn: ({ signal }) => fetchServiceOrder(id, signal),
  })

export const servicePolicyQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'service-policy'],
    queryFn: ({ signal }) => fetchServicePolicy(signal),
  })

export const serviceCategoriesQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'service-categories'],
    queryFn: ({ signal }) => fetchServiceCategories(signal),
  })

/** Every service that is not deleted, active or not. The order form uses `serviceItemsQuery`. */
export const catalogQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'catalog'],
    queryFn: ({ signal }) => fetchCatalog(signal),
  })

export const serviceItemsQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'service-items'],
    queryFn: ({ signal }) => fetchServiceItems(signal),
  })

/** Keyed by the basket, so a basket priced before comes from the cache. */
export const orderPreviewQuery = (applicationId: string, input: AdminOrderInput) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', applicationId, 'service-preview', input],
    queryFn: ({ signal }) => previewOrder(applicationId, input, signal),
    retry: false,
  })

export const companiesQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'companies'],
    queryFn: ({ signal }) => fetchCompanies(signal),
  })

export const teamQuery = (companyId: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'companies', companyId],
    queryFn: ({ signal }) => fetchTeam(companyId, signal),
  })

/** The presigned URL lives 300 s; refetched well inside that so a preview never 403s. */
export const companyLogoQuery = (key: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'company-logo', key],
    queryFn: ({ signal }) => fetchCompanyLogoUrl(key, signal),
    staleTime: 240_000,
  })

/** The free badge types a convention pass can be issued against. Empty when `ticket` is off. */
export const badgeTypesQuery = () =>
  queryOptions({
    queryKey: ['exhibitor', 'badge-types'],
    queryFn: ({ signal }) => fetchBadgeTypes(signal),
  })

export const passesQuery = (applicationId: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', applicationId, 'passes'],
    queryFn: ({ signal }) => fetchPasses(applicationId, signal),
  })

export const fitOutQuery = (applicationId: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', applicationId, 'fit-out'],
    queryFn: ({ signal }) => fetchFitOut(applicationId, signal),
  })

/** The link lives 300 s; refetched well inside that so a preview never 403s. */
export const fitOutFileLinkQuery = (applicationId: string, file: FitOutFile) =>
  queryOptions({
    queryKey: ['exhibitor', 'applications', applicationId, 'fit-out', 'file-link', file],
    queryFn: () => fetchFitOutFileLink(applicationId, file),
    staleTime: 240_000,
  })

/** Null for a registration that no stand issued: the API's 404 is the answer, not a failure. */
export const standOfRegistrationQuery = (registrationId: string) =>
  queryOptions({
    queryKey: ['exhibitor', 'pass-registrations', registrationId],
    queryFn: async ({ signal }) => {
      try {
        return await fetchStandOfRegistration(registrationId, signal)
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 404) return null
        throw error
      }
    },
    retry: false,
  })
